import { useMutation, useQuery } from "@tanstack/react-query";
import { getCommission } from "../../../data";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import AuthStore from "@/stores/AuthStore";
import { deleteCommission } from "@/data/requests/commission-requests";
import {
  User,
  Mail,
  FileText,
  DollarSign,
  MapPin,
  Calendar,
  Eye,
  Trash2,
  Receipt,
} from "lucide-react";

const STATUS_COLORS = {
  "Mail Sended": "bg-gray-500",
  "Invoice Uploaded": "bg-green-500",
  Troubled: "bg-yellow-600",
  "Change Requested": "bg-red-600",
  Approved: "bg-blue-600",
  Accounting: "bg-purple-600",
  Paid: "bg-emerald-600",
  LUCA: "bg-orange-600",
  LUCAREADY: "bg-teal-600",
};

export function CommissionDetailScreen() {
  const { id, commissionId } = useParams();
  const [deleteInvoiceOpen, setDeleteInvoiceOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["commissionDetail", commissionId],
    queryFn: async () => await getCommission(id, commissionId),
  });

  const { mutate: deleteInvoice } = useMutation({
    mutationFn: async () => await deleteCommission(commission.id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Commission deleted successfully",
      });
      navigate(-1);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Receipt className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg font-medium">Loading commission...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <p className="text-lg font-medium text-destructive">
              Error: {error.message}
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const commission = data.data;

  const handleDeleteInvoice = () => {
    deleteInvoice(commission.id);
    setDeleteInvoiceOpen(false);
  };

  const RESTRICTED_STATUSES = ["accounting", "luca", "paid", "inbank", "lucaready"];
  const canDelete =
    AuthStore.isHavePermission("PAYMENT_DELETE") &&
    (!commission?.commisionDetail?.status || 
     !RESTRICTED_STATUSES.includes(commission?.commisionDetail?.status?.toLowerCase()));
     

  return (
    <AppLayout title={`Commission Detail - ${commission.influencerName}`}>
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header Card with Status */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-5 w-5 text-blue-600" />
                Commission Information
              </CardTitle>
              <Badge
                className={`${
                  STATUS_COLORS[commission.status] || "bg-gray-500"
                } text-white`}
              >
                {commission.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Influencer Info */}
            <div>
              <Label htmlFor="influencerName" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Influencer Name
              </Label>
              <Input
                id="influencerName"
                value={commission.influencerName}
                readOnly
                className="mt-1.5 font-medium"
              />
            </div>

            {/* Email & Invoice ID */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  value={commission.email}
                  readOnly
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="invoiceId" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Invoice ID
                </Label>
                <Input
                  id="invoiceId"
                  value={commission.invoiceId}
                  readOnly
                  className="mt-1.5 font-mono"
                />
              </div>
            </div>

            {/* Amount & Country */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="finalAmount" className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Final Amount
                </Label>
                <Input
                  id="finalAmount"
                  value={`$${commission.finalAmount?.toFixed(2) || "0.00"}`}
                  readOnly
                  className="mt-1.5 font-bold text-emerald-600"
                />
              </div>

              <div>
                <Label htmlFor="country" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Country
                </Label>
                <Input
                  id="country"
                  value={commission.country || "N/A"}
                  readOnly
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="createdAt" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Created At
                </Label>
                <Input
                  id="createdAt"
                  value={new Date(commission.createdAt).toLocaleString()}
                  readOnly
                  className="mt-1.5 text-sm"
                />
              </div>

              <div>
                <Label htmlFor="updatedAt" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Updated At
                </Label>
                <Input
                  id="updatedAt"
                  value={new Date(commission.updatedAt).toLocaleString()}
                  readOnly
                  className="mt-1.5 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-purple-600" />
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="outline">
                <Link to={`/invoice/${commission.uploadPath}`}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Invoice Preview
                </Link>
              </Button>

              {canDelete && (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteInvoiceOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Commission
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteInvoiceOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                commission for <strong>{commission.influencerName}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteInvoiceOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteInvoice}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
